import React, { useState, useRef, useEffect } from "react";
import { useEmployerJobApi } from "../../../_common/hooks/actions/job/appEmployerJobApiHook";
import { useToaster } from "../../../_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "../../../_common/hooks/useTranslation";
import { useAppUserDataSelector } from "../../../_common/hooks/selectors/userSelector";

interface BulkResumeUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId?: string;
}

export default function BulkResumeUploadModal({ isOpen, onClose, jobId }: BulkResumeUploadModalProps) {
  const { t } = useTranslation("job-details");
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hooks
  const employerJobApi = useEmployerJobApi();
  const toast = useToaster();
  const userData = useAppUserDataSelector();

  // Check if user is on free plan
  const isFreePlan = userData?.subscription?.price === 0;
  const FREE_PLAN_MAX_DOCS = 10;

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files: File[]) => {
    // Filter for PDF files only
    const validFiles = files.filter((file) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();
      return fileType === "application/pdf" || fileName.endsWith(".pdf");
    });

    // Check if user is on free plan and validate file count
    if (isFreePlan) {
      const totalFiles = selectedFiles.length + validFiles.length;
      if (totalFiles > FREE_PLAN_MAX_DOCS) {
        toast.error(
          t("jobDetails.talentScouting.bulkUpload.freePlanLimitError", {
            limit: FREE_PLAN_MAX_DOCS,
          }),
        );
        return;
      }
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) {
      toast.error(t("jobDetails.talentScouting.bulkUpload.selectFilesError"));
      return;
    }

    if (!jobId) {
      toast.error(t("jobDetails.talentScouting.bulkUpload.jobIdError"));
      return;
    }

    setIsUploading(true);

    employerJobApi.callBulkResumeUpload(
      jobId, // This is the job_emp_id
      selectedFiles,
      (message: string, response: any) => {
        // Success callback
        console.log("Bulk resume upload successful:", response);
        toast.success(message || t("jobDetails.talentScouting.bulkUpload.uploadSuccess"));

        // Reset and close
        setSelectedFiles([]);
        setIsUploading(false);
        onClose();
      },
      (errorMessage: string) => {
        // Error callback
        console.error("Bulk resume upload failed:", errorMessage);
        toast.error(errorMessage || t("jobDetails.talentScouting.bulkUpload.uploadError"));
        setIsUploading(false);
      },
    );
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    onClose();
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="modal-overlay">
      <div className="bulk-upload-modal">
        <div className="modal-header d-block">
          <h3>{t("jobDetails.talentScouting.bulkUpload.title")}</h3>
          <p>{t("jobDetails.talentScouting.bulkUpload.subtitle")}</p>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {/* <div className="folder-section">
            <div className="folder-icon">
              <i className="fa fa-folder"></i>
              <span>My CV Folders</span>
            </div>
          </div> */}

          <div
            className={`upload-area ${dragActive ? "drag-active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={handleBrowseFiles}
            style={{ cursor: "pointer" }}
          >
            <div className="upload-icon">
              <i className="fa fa-cloud-upload"></i>
            </div>
            <div className="upload-text">
              <p>{t("jobDetails.talentScouting.bulkUpload.dragText")}</p>
              <small>{t("jobDetails.talentScouting.bulkUpload.fileTypes")}</small>
            </div>
          </div>

          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h4>{t("jobDetails.talentScouting.bulkUpload.selectedFiles")}</h4>
              <div className="file-list">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <button className="remove-file-btn" onClick={() => removeFile(index)}>
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="upload-note">
            <p>
              <span className="note-highlight">{t("jobDetails.talentScouting.bulkUpload.noteHighlight")}</span>
              <span className="note-text"> {t("jobDetails.talentScouting.bulkUpload.noteText")}</span>
            </p>
          </div>

          {/* Important Note Section - Styled like Set Validity Modal */}
          <div
            className="alert alert-info mb-3"
            style={{
              backgroundColor: "#e7f3ff",
              borderLeft: "4px solid #2196F3",
              padding: "15px 20px",
              borderRadius: "4px",
              marginTop: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <i
                className="fa fa-info-circle"
                style={{
                  fontSize: "20px",
                  color: "#2196F3",
                  marginRight: "12px",
                  marginTop: "2px",
                }}
              ></i>
              <div>
                <p style={{ margin: 0, color: "#555", lineHeight: "1.6", fontSize: "15px" }}>
                  <strong style={{ color: "#1976D2" }}>
                    {t("jobDetails.talentScouting.bulkUpload.importantNote")}
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={handleCancel} disabled={isUploading}>
            {t("jobDetails.talentScouting.bulkUpload.cancel")}
          </button>
          <button className="btn-preview" onClick={handleUpload} disabled={isUploading}>
            {isUploading ? (
              <>
                <i className="fa fa-spinner fa-spin"></i> {t("jobDetails.talentScouting.bulkUpload.uploading")}
              </>
            ) : (
              t("jobDetails.talentScouting.bulkUpload.uploadButton")
            )}
          </button>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
      </div>
    </div>
  );
}
